import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const LOGO = "/logo-geekcreative.png";

export default function Layout({ children }) {
  const { user, logout, stopImpersonate } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const isImpersonating = !!user?.is_impersonating;
  const handleLogout = async () => { await logout(); nav("/login"); };
  const handleStopImp = async () => {
    try {
      await stopImpersonate();
      toast.success("Returned to admin");
      nav("/admin");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Could not return to admin — please sign in again.");
    }
  };

  const navItems = isAdmin
    ? [
        { to: "/admin", label: "Pipeline" },
        { to: "/admin/customers", label: "Customers" },
        { to: "/admin/products", label: "Products" },
        { to: "/admin/settings", label: "Settings" },
      ]
    : [
        { to: "/dashboard", label: "My Jobs" },
        { to: "/request-quote", label: "New Request" },
      ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-[var(--gc-navy)]">
      {isImpersonating && (
        <div
          className="sticky top-0 z-50 bg-[var(--gc-orange)] text-white px-6 py-2.5 flex items-center justify-between gap-4 text-sm shadow-md"
          data-testid="impersonation-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Eye size={18} weight="bold" className="shrink-0" />
            <span className="font-semibold truncate">
              Viewing as <span className="underline">{user?.first_name} {user?.last_name}</span>
              <span className="opacity-80"> · {user?.email}</span>
            </span>
            <span className="hidden md:inline opacity-80 font-mono-tech uppercase text-xs tracking-widest">
              admin: {user?.impersonator_email}
            </span>
          </div>
          <button
            onClick={handleStopImp}
            className="bg-white text-[var(--gc-navy)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[var(--gc-navy)] hover:text-white transition-colors shrink-0"
            data-testid="stop-impersonation-btn"
          >
            <X size={14} weight="bold" />
            Return to admin
          </button>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[var(--gc-border)]" style={isImpersonating ? { top: '44px' } : undefined}>
        <div className="max-w-[1400px] mx-auto px-8 h-24 flex items-center justify-between gap-6">
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center gap-4 shrink-0" data-testid="header-logo">
            <img src={LOGO} alt="GeekCreative" className="h-16 md:h-20 w-auto block" />
            {isAdmin && <span className="chip chip-navy hidden lg:inline-flex">Admin</span>}
          </Link>
          <nav className="flex items-center gap-8 text-sm">
            {navItems.map((n) => {
              const active = loc.pathname === n.to || (n.to !== "/admin" && loc.pathname.startsWith(n.to));
              return (
                <Link key={n.to} to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`link-underline transition-colors ${active ? 'text-[var(--gc-orange)]' : 'text-[var(--gc-navy)]'}`}>
                  {n.label}
                </Link>
              );
            })}
            <div className="hidden md:flex items-center gap-4 ml-4 pl-6 border-l border-[var(--gc-border)]">
              <div className="text-right hidden lg:block">
                <div className="text-sm font-semibold">{user?.first_name} {user?.last_name}</div>
                <div className="tech-label leading-none">{user?.business_name || user?.role}</div>
              </div>
              <button onClick={handleLogout} data-testid="logout-btn" className="btn-secondary px-3 py-2 text-xs">
                Logout
              </button>
            </div>
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-[var(--gc-border)] py-8 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between text-xs font-mono-tech uppercase text-[var(--gc-text-muted)]">
          <div>© {new Date().getFullYear()} Borrow A Geek · GeekCreative</div>
          <div>065 671 2772 · hello@geekcreative.ie</div>
        </div>
      </footer>
    </div>
  );
}
